import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

const storeToken = (data) => {
  try {
    if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
  } catch { /* ignore */ }
};

export function AuthProvider({ children }) {
  // null = checking, object = authenticated, false = not authenticated
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      let hadToken = false;
      try { hadToken = !!localStorage.getItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      setUser(false);
      if (hadToken && typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login?expired=1");
      }
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback handles the session exchange.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storeToken(data);
    setUser(data);
    return data;
  };

  const register = async (name, email, password, ref) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...(ref ? { ref } : {}) });
    storeToken(data);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh: checkAuth, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
